/**
 * SectionBody — renders the active section's row table + section
 * summary editor + AI Suggest button + per-section explicit Save.
 *
 * Concerns owned by this component:
 *   - List the rows of the active section.
 *   - Open the row drawer for inline create / edit / delete.
 *   - Show required-field hints from the catalog.
 *   - Wire "Save Section" (explicit, no autosave per Q8) and emit
 *     dirty-state changes to the parent so the editor banner reflects
 *     unsaved changes.
 *   - Show "AI Suggest" → opens the row drawer pre-populated with the
 *     mock draft, never auto-applies.
 */
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Save, Sparkles, Pencil, Trash2, Layers } from 'lucide-react';
import { RowEditorDrawer } from './RowEditorDrawer';
import { CareDomainDrawer } from './CareDomainDrawer';

const DOMAIN_SECTIONS = { personal_care: 'Personal Care', nutrition: 'Nutrition' };

const domainsEnabled = () => {
  try {
    const flags = JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}');
    return !!flags.participant_careplan_domains_enabled;
  } catch { return false; }
};

const RISK_TONE = {
  LOW: 'bg-zinc-50 text-zinc-700 border-zinc-200',
  MEDIUM: 'bg-amber-50 text-amber-700 border-amber-200',
  HIGH: 'bg-rose-50 text-rose-700 border-rose-200',
  CRITICAL: 'bg-rose-100 text-rose-900 border-rose-300',
};

export function SectionBody({
  catalog,
  section,
  canEdit,
  onRowCreate,
  onRowUpdate,
  onRowDelete,
  onSaveSection,
  markDirty,
  onAiSuggest,
  planId,
  participantId,
  canEditDomain,
}) {
  // Track the section_key so we can reset local state when the active
  // section changes — done in render via the standard "key on derived
  // identity" trick rather than a state-resetting useEffect, which
  // trips React 19's `set-state-in-effect` rule.
  const [keyState, setKeyState] = useState(() => ({
    section_key: section?.section_key || catalog?.key,
    summary: section?.summary || '',
  }));
  if (keyState.section_key !== (section?.section_key || catalog?.key)) {
    setKeyState({
      section_key: section?.section_key || catalog?.key,
      summary: section?.summary || '',
    });
  }
  const summary = keyState.summary;
  const setSummary = (v) => setKeyState((s) => ({ ...s, summary: v }));

  const [drawer, setDrawer] = useState({ open: false, row: null });
  const [domainDrawer, setDomainDrawer] = useState({ open: false, row: null });
  const [aiBusy, setAiBusy] = useState(false);
  const sectionKey = section?.section_key || catalog?.key;
  const isDomainSection = domainsEnabled() && !!DOMAIN_SECTIONS[sectionKey];

  if (!catalog) return null;
  const rows = section?.rows || [];
  const required = catalog.required_fields || [];

  const onSummaryChange = (v) => {
    setSummary(v);
    markDirty?.(v !== (section?.summary || ''));
  };

  const saveSection = async () => {
    await onSaveSection({ summary });
    markDirty?.(false);
  };

  const requestAiDraft = async () => {
    setAiBusy(true);
    try {
      const draft = await onAiSuggest({ userBrief: summary });
      if (draft) {
        setDrawer({
          open: true,
          row: {
            observation_or_need: draft.title || `[MOCK] ${catalog.title}`,
            intervention_or_staff_instruction:
              draft.body || draft.intervention || JSON.stringify(draft).slice(0, 800),
            portal_visible: !!catalog.portal_default,
            shift_visible: !!catalog.shift_default,
            _ai_draft: true,
          },
        });
      }
    } finally { setAiBusy(false); }
  };

  return (
    <Card data-testid={`structured-section-body-${catalog.key}`}>
      <CardContent className="p-4 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold text-base" data-testid="structured-active-section-title">
              {section?.title_override || catalog.title}
            </h3>
            {section?.summary ? (
              <p className="text-xs text-muted-foreground mt-0.5" data-testid="structured-active-section-summary">
                {section.summary}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-1 mt-1">
              {required.length > 0 && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
                  Required: {required.join(', ')}
                </Badge>
              )}
              {catalog.shift_brief_eligible && (
                <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[10px]">
                  shows in Staff Shift Brief
                </Badge>
              )}
              {(section?.portal_visible != null ? section.portal_visible : catalog.portal_default) ? (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]" data-testid="structured-active-section-portal-badge">
                  portal-visible
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-zinc-100 text-zinc-500 border-zinc-200 text-[10px]" data-testid="structured-active-section-portal-badge">
                  portal-hidden
                </Badge>
              )}
              {section?.category_tag && (
                <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]" data-testid="structured-active-section-category-badge">
                  {section.category_tag === 'CUSTOM'
                    ? (section.category_label || 'CUSTOM')
                    : (section.category_tag === 'HIGH_RISK' ? 'HIGH RISK' : section.category_tag)}
                </Badge>
              )}
              {catalog.clinical_only && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]">
                  clinical extension
                </Badge>
              )}
              {catalog.permission_gate && (
                <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">
                  perm: {catalog.permission_gate}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            {canEdit && (
              <Button size="sm" variant="outline"
                      onClick={requestAiDraft}
                      disabled={aiBusy}
                      data-testid={`structured-ai-suggest-${catalog.key}`}>
                <Sparkles className="h-3 w-3 mr-1" /> {aiBusy ? 'Drafting…' : 'AI Suggest'}
              </Button>
            )}
            {canEdit && (
              <Button size="sm"
                      onClick={() => setDrawer({ open: true, row: null })}
                      data-testid={`structured-add-row-${catalog.key}`}>
                <Plus className="h-3 w-3 mr-1" /> Add Row
              </Button>
            )}
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-zinc-700">Section summary</label>
          <textarea
            value={summary}
            onChange={(e) => onSummaryChange(e.target.value)}
            disabled={!canEdit}
            rows={2}
            placeholder="Optional short summary that frames this section…"
            className="mt-1 w-full text-sm border rounded px-2 py-1"
            data-testid={`structured-section-summary-${catalog.key}`}
          />
        </div>

        {rows.length === 0 ? (
          <div className="text-xs italic text-muted-foreground border border-dashed rounded p-4 text-center"
               data-testid={`structured-empty-${catalog.key}`}>
            No rows yet. Add the first observation → goal → intervention row for this section.
          </div>
        ) : (
          <ul className="space-y-2" data-testid={`structured-row-list-${catalog.key}`}>
            {rows.map((row) => (
              <li key={row.id} className="border rounded p-3" data-testid={`structured-row-${row.id}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 space-y-1">
                    <div className="text-xs font-mono text-muted-foreground">{row.id}</div>
                    {row.observation_or_need && (
                      <div className="text-sm"><span className="font-semibold">Observation:</span> {row.observation_or_need}</div>
                    )}
                    {row.goal_or_desired_outcome && (
                      <div className="text-sm"><span className="font-semibold">Goal:</span> {row.goal_or_desired_outcome}</div>
                    )}
                    {row.intervention_or_staff_instruction && (
                      <div className="text-sm"><span className="font-semibold">Intervention:</span> {row.intervention_or_staff_instruction}</div>
                    )}
                    <div className="flex flex-wrap gap-1 mt-1">
                      <Badge variant="outline" className={`text-[10px] ${RISK_TONE[row.risk_level] || 'bg-zinc-50 text-zinc-700 border-zinc-200'}`}>
                        risk · {row.risk_level || 'LOW'}
                      </Badge>
                      <Badge variant="outline" className="bg-zinc-50 text-zinc-700 border-zinc-200 text-[10px]">
                        {row.support_level || 'SUPERVISION'}
                      </Badge>
                      <Badge variant="outline" className="bg-zinc-50 text-zinc-700 border-zinc-200 text-[10px]">
                        {row.frequency || 'as_required'}
                      </Badge>
                      {row.portal_visible && (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">portal</Badge>
                      )}
                      {row.shift_visible && (
                        <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[10px]">shift</Badge>
                      )}
                      {row.escalation_required && (
                        <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">escalation</Badge>
                      )}
                      {row._internal_notes_redacted && (
                        <Badge variant="outline" className="bg-zinc-100 text-zinc-600 border-zinc-300 text-[10px]" title="Internal notes hidden — requires careplans.row.notes.view_internal">
                          internal notes hidden
                        </Badge>
                      )}
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex gap-1">
                      {isDomainSection && (
                        <Button size="icon" variant="ghost"
                                onClick={() => setDomainDrawer({ open: true, row })}
                                title="Care domain"
                                data-testid={`structured-domain-row-${row.id}`}>
                          <Layers className="h-3 w-3 text-sky-600" />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost"
                              onClick={() => setDrawer({ open: true, row })}
                              data-testid={`structured-edit-row-${row.id}`}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button size="icon" variant="ghost"
                              onClick={() => onRowDelete(row.id)}
                              data-testid={`structured-delete-row-${row.id}`}>
                        <Trash2 className="h-3 w-3 text-rose-600" />
                      </Button>
                    </div>
                  )}
                  {!canEdit && isDomainSection && (
                    <Button size="icon" variant="ghost"
                            onClick={() => setDomainDrawer({ open: true, row })}
                            title="Care domain"
                            data-testid={`structured-domain-row-${row.id}`}>
                      <Layers className="h-3 w-3 text-sky-600" />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {canEdit && (
          <div className="text-right">
            <Button size="sm" variant="secondary" onClick={saveSection}
                    data-testid={`structured-save-section-${catalog.key}`}>
              <Save className="h-3 w-3 mr-1" /> Save Section
            </Button>
          </div>
        )}
      </CardContent>

      {drawer.open && (
        <RowEditorDrawer
          initial={drawer.row}
          onClose={() => setDrawer({ open: false, row: null })}
          onSubmit={async (payload) => {
            if (drawer.row?.id) await onRowUpdate(drawer.row.id, payload);
            else await onRowCreate(payload);
            setDrawer({ open: false, row: null });
          }}
          sectionMeta={catalog}
        />
      )}
      {domainDrawer.open && (
        <CareDomainDrawer
          open={domainDrawer.open}
          onClose={() => setDomainDrawer({ open: false, row: null })}
          planId={planId}
          sectionKey={sectionKey}
          row={domainDrawer.row}
          participantId={participantId}
          canEdit={!!canEditDomain && canEdit}
          onSaved={(updatedRow) => setDomainDrawer({ open: true, row: updatedRow })}
        />
      )}
    </Card>
  );
}

export default SectionBody;
